import { useGetUniversityIdpQuery } from "@api/university";
import { useWhoamiQuery } from "@api/user";
import { Role } from "@onetutor/shared/user";
import { useAuth } from "react-oidc-context";
import { useSearchParams } from "react-router";
import { environment } from "../environment";

export const useLogin = ({
  isGuestLogin = false,
}: {
  isGuestLogin?: boolean;
} = {}) => {
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");

  const { data: university } = useGetUniversityIdpQuery();

  return async () => {
    if (redirect) {
      sessionStorage.setItem("redirectAfterLogin", redirect);
    }

    await auth.signinRedirect({
      redirectMethod: "replace",
      redirect_uri: environment.oidc.redirect_uri,
      extraQueryParams:
        !isGuestLogin && university?.idp
          ? { identity_provider: university.idp }
          : undefined,
    });
  };
};

export const useLogout = () => {
  const auth = useAuth();

  return async () => {
    await auth.signoutRedirect({
      redirectMethod: "replace",
      extraQueryParams: {
        logout_uri: `${window.location.origin}/login`,
        client_id: environment.oidc.client_id,
      },
    });
  };
};

export const useUserData = () => {
  const auth = useAuth();
  const { data: user, isLoading } = useWhoamiQuery(undefined, {
    skip: !auth.isAuthenticated || !auth.user,
  });

  const privacyConsentRequired = user ? !user.privacyConsent : false;

  return {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading || isLoading,
    user,
    privacyConsentRequired,
  };
};

export const useUserRole = (): Role | null => {
  const { user } = useUserData();
  return user?.role ?? null;
};

export const useIsUserRole = (...roles: Role[]): boolean => {
  const userRole = useUserRole();
  return roles.some((role) => userRole === role);
};

export const useIsUserManagingAnyCourse = (): boolean => {
  const { user } = useUserData();
  return user?.isManagingAnyCourse ?? false;
};
